import React, { useEffect, useRef, useState } from 'react';
import LazyImage from './LazyImage';

/**
 * Shows the first product image and, while hovered, continuously scrolls
 * through the images from right to left in a seamless loop.
 */
const HoverImageScroll = ({
  images = [],
  alt = '',
  width = 1200,
  height = 1500,
  intervalMs = 1100,
}) => {
  const validImages = (images || []).filter(Boolean);
  const hasMultiple = validImages.length > 1;

  // Duplicate the first image at the end so we can loop seamlessly forward.
  const slides = hasMultiple ? [...validImages, validImages[0]] : validImages;

  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const timerRef = useRef(null);

  const stopScrolling = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleEnter = () => {
    if (!hasMultiple) return;
    stopScrolling();
    timerRef.current = setInterval(() => {
      setAnimate(true);
      setIndex((prev) => prev + 1);
    }, intervalMs);
  };

  const handleLeave = () => {
    stopScrolling();
    setAnimate(false);
    setIndex(0);
  };

  // When we reach the duplicated first slide, jump back to the real first
  // slide without animation so the loop looks continuous (always left).
  const handleTransitionEnd = () => {
    if (index >= slides.length - 1) {
      setAnimate(false);
      setIndex(0);
    }
  };

  // Re-enable the transition on the next frame after a no-animation reset.
  useEffect(() => {
    if (!animate) {
      const id = requestAnimationFrame(() => setAnimate(true));
      return () => cancelAnimationFrame(id);
    }
  }, [animate]);

  useEffect(() => stopScrolling, []);

  if (validImages.length === 0) {
    return (
      <div className="relative aspect-[4/5] overflow-hidden bg-white">
        <LazyImage src={null} alt={alt} width={width} height={height} fit="cover" />
      </div>
    );
  }

  return (
    <div
      className="relative aspect-[4/5] overflow-hidden bg-white"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <div
        className={`absolute inset-0 flex flex-row ${animate ? 'transition-transform duration-700 ease-in-out' : ''}`}
        style={{ transform: `translateX(-${index * 100}%)` }}
        onTransitionEnd={handleTransitionEnd}
      >
        {slides.map((img, i) => (
          <div key={i} className="relative w-full h-full flex-shrink-0">
            <LazyImage
              src={img}
              alt={i === 0 ? alt : `${alt} ${i + 1}`}
              width={width}
              height={height}
              fit="cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default HoverImageScroll;
